'use client';

import TitleBar from '../layout/TitleBar';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Grid, Button, Modal, ImageList, ImageListItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import { orderBy } from 'lodash';
import OutageTable from '@/components/outage/OutageTable';
import GenericFormModal from '@/components/form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '@/components/form/GenericForm';
import { useOutages } from '@/hooks/api/useOutages';
import PreviewIcon from '@mui/icons-material/Preview';
import CloseIcon from '@mui/icons-material/Close';
import { useLocationReadings, useGenerateReport, useGoaEvents, useBulkUpdateGoaEvents, useReportsByDate, useUpsertGoaEvents, useUpsertTrkEvents, useUpsertAtmEvents, useLocationDetails } from '@/hooks/useLocation';
import { useQueryClient } from '@tanstack/react-query';
import { useStartLocationVisit } from '@/hooks/api/useLocationVisits';
import { GOA_EVENT_ENTRY_TYPE } from '@/stack-shared/interfaces/model/goa-event.interface';
import { getAuthData } from '@/hooks/getAuthData';
import HttpClient from '@/api/HttpClient';

// Helper function to format currency values
const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

// Saved Reports Section Component
const SavedReportsSection = ({ locationId, serviceDate }: { locationId: string | number; serviceDate: string }) => {
  const { data: reportsData, isLoading } = useReportsByDate(locationId, serviceDate);
  const [selectedReportHtml, setSelectedReportHtml] = useState<string>('');
  const [savedReportModalVisible, setSavedReportModalVisible] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (isLoading) {
    return (
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom>Saved Reports</Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2">Loading reports...</Typography>
      </Box>
    );
  }

  const reports = reportsData?.reports || [];

  if (reports.length === 0) {
    return null; // Don't show section if no reports
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleViewReport = (report: any) => {
    setSelectedReportHtml(report.content || '');
    setSavedReportModalVisible(true);
  };

  return (
    <>
      <Box sx={{ mt: 6 }}>
        <Typography variant="h6" gutterBottom>Saved Reports</Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Created At</strong></TableCell>
                <TableCell><strong>Generated By</strong></TableCell>
                <TableCell><strong>Comments</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report: any) => (
                <TableRow key={report.id}>
                  <TableCell>{formatDate(report.createdAt)}</TableCell>
                  <TableCell>
                    {report.generatedByFirstName && report.generatedByLastName
                      ? `${report.generatedByFirstName} ${report.generatedByLastName}`
                      : 'Unknown'}
                  </TableCell>
                  <TableCell>{report.comments || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewReport(report)}
                      title="View Report"
                    >
                      <PreviewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Report View Modal */}
      <Modal
        open={savedReportModalVisible}
        onClose={() => setSavedReportModalVisible(false)}
        aria-labelledby="saved-report-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 0,
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '100%',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <Typography variant="h6" id="saved-report-modal-title">
              Saved Report
            </Typography>
            <IconButton
              onClick={() => setSavedReportModalVisible(false)}
              size="large"
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            {selectedReportHtml ? (
              <Box
                sx={{
                  ...(isDark && {
                    '& *': { color: '#fff !important' },
                    '& td': { borderBottomColor: 'rgba(255,255,255,0.2) !important' },
                    '& .summary': { background: 'rgba(255,255,255,0.12) !important' },
                  }),
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: selectedReportHtml }} />
              </Box>
            ) : (
              <Typography variant="body2">No content available</Typography>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

function LocationServiceViewPage() {
  const { locationId, serviceDate } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { data: locationDetails } = useLocationDetails(locationId || '');
  const location = locationDetails?.location;
  const { data: outagesData } = useOutages();
  const outages = useMemo(() => {
    if (!outagesData) return [];
    return Array.isArray(outagesData) ? outagesData : Object.values(outagesData);
  }, [outagesData]);

  const [bulkEditVisible, setBulkEditVisible] = useState(false);
  const [bulkEditTrkVisible, setBulkEditTrkVisible] = useState(false);
  const [bulkEditAtmVisible, setBulkEditAtmVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [locationVisitId, setLocationVisitId] = useState(null as any);
  const [html, setHtml] = useState('');
  const [visitPhotos, setVisitPhotos] = useState<any[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [theftReports, setTheftReports] = useState<any[]>([]);
  const [isLoadingTheftReports, setIsLoadingTheftReports] = useState(false);
  const httpClient = new HttpClient();

  const formattedDisplayDate = new Date(serviceDate!).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Use React Query hooks
  const { data: locationData, isLoading, error, refetch: refetchLocationData } = useLocationReadings(locationId!, serviceDate);
  const generateReport = useGenerateReport();
  const bulkUpdateGoaEvents = useBulkUpdateGoaEvents();
  const upsertGoaEventsMutation = useUpsertGoaEvents();
  const upsertTrkEventsMutation = useUpsertTrkEvents();
  const upsertAtmEventsMutation = useUpsertAtmEvents();
  
  // Get locationVisitId from locationData (set in useEffect below)
  const [locationVisitIdForGoaEvents, setLocationVisitIdForGoaEvents] = useState<number | undefined>(undefined);
  
  // Get GOA events data for better display - use locationVisitId to filter events for this specific visit
  // This must be called before the useEffect that uses refetchGoaEvents
  const { data: goaEventsData, refetch: refetchGoaEvents } = useGoaEvents(
    locationId!, 
    serviceDate!,
    undefined, // onlyActive
    locationVisitIdForGoaEvents // Pass locationVisitId to filter events for this specific visit
  );
  
  // Helper function to get asset key from resource
  const getAssetKey = (resource: any): string => {
    if (resource.model && resource.modelId) {
      return `${resource.model}:${resource.modelId}`;
    }
    return '';
  };

  // Helper function to get asset ID and position from locationData
  const getAssetInfo = (resource: any): { assetId: string | null; position: number | null } => {
    const key = getAssetKey(resource);
    if (!key || !locationData) return { assetId: null, position: null };

    const [model, modelIdStr] = key.split(':');
    const modelId = parseInt(modelIdStr, 10);

    if (model === 'goa') {
      const goa = locationData.goas?.find((g: any) => g.id === modelId);
      return { assetId: goa?.assetId || null, position: goa?.position || null };
    } else if (model === 'trk') {
      const trk = locationData.trks?.find((t: any) => t.id === modelId);
      return { assetId: trk?.assetId || null, position: trk?.position || null };
    } else if (model === 'atm') {
      const atm = locationData.atms?.find((a: any) => a.id === modelId);
      return { assetId: atm?.assetId || null, position: atm?.position || null };
    }

    return { assetId: null, position: null };
  };

  // Sort photos by position first, then by asset ID
  const sortPhotosByAsset = (photos: any[]): any[] => {
    return [...photos].sort((photo1, photo2) => {
      const info1 = getAssetInfo(photo1);
      const info2 = getAssetInfo(photo2);

      // Sort by position first (if both have positions)
      if (info1.position !== null && info2.position !== null) {
        if (info1.position !== info2.position) {
          return info1.position - info2.position;
        }
      } else if (info1.position !== null) {
        return -1; // photo1 has position, photo2 doesn't - photo1 comes first
      } else if (info2.position !== null) {
        return 1; // photo2 has position, photo1 doesn't - photo2 comes first
      }

      // If positions are equal or both nil, sort by asset ID
      const assetId1 = info1.assetId || '';
      const assetId2 = info2.assetId || '';
      return assetId1.localeCompare(assetId2);
    });
  };
  
  // Update locationVisitIdForGoaEvents when locationData changes
  useEffect(() => {
    if (locationData?.locationVisitId) {
      setLocationVisitIdForGoaEvents(locationData.locationVisitId);
      // Refetch GOA events when locationVisitId becomes available
      refetchGoaEvents();
    }
  }, [locationData?.locationVisitId, refetchGoaEvents]);

  // Update locationVisitId when locationData changes (separate from serviceData computation)
  useEffect(() => {
    console.log('🔵 LocationServiceViewPage: locationData changed, locationVisitId:', locationData?.locationVisitId);
    if (locationData?.locationVisitId) {
      console.log('🔵 LocationServiceViewPage: Setting locationVisitId to:', locationData.locationVisitId);
      setLocationVisitId(locationData.locationVisitId);
    } else {
      console.log('🔵 LocationServiceViewPage: No locationVisitId in locationData');
    }
  }, [locationData?.locationVisitId]);

  // Load photos when locationVisitId is available
  useEffect(() => {
    const loadVisitPhotos = async () => {
      if (!locationVisitId) {
        console.log('🔵 LocationServiceViewPage: No locationVisitId, skipping photo load');
        setVisitPhotos([]);
        return;
      }

      console.log('🔵 LocationServiceViewPage: Loading photos for locationVisitId:', locationVisitId);
      setIsLoadingPhotos(true);
      try {
        const response = await httpClient.get('api/resource/get-by-location-visit', {
          locationVisitId: locationVisitId,
          contexts: 'black-box,game-accounting'
        });
        
        console.log('🔵 LocationServiceViewPage: Photo response:', response.data);
        if (response.data?.resources) {
          console.log('🔵 LocationServiceViewPage: Found', response.data.resources.length, 'photos');
          setVisitPhotos(response.data.resources);
        } else {
          console.log('🔵 LocationServiceViewPage: No resources in response');
          setVisitPhotos([]);
        }
      } catch (error) {
        console.error('🔴 LocationServiceViewPage: Failed to load visit photos:', error);
        setVisitPhotos([]);
      } finally {
        setIsLoadingPhotos(false);
      }
    };

    loadVisitPhotos();
  }, [locationVisitId]);

  // Sort photos when locationData becomes available
  useEffect(() => {
    if (visitPhotos.length > 0 && locationData) {
      const sortedPhotos = sortPhotosByAsset(visitPhotos);
      // Only update if order changed
      const orderChanged = sortedPhotos.some((photo, index) => photo.id !== visitPhotos[index]?.id);
      if (orderChanged) {
        setVisitPhotos(sortedPhotos);
      }
    }
  }, [locationData, visitPhotos.length]);

  // Load theft reports when locationId and serviceDate are available
  useEffect(() => {
    const loadTheftReports = async () => {
      if (!locationId || !serviceDate) {
        setTheftReports([]);
        return;
      }

      setIsLoadingTheftReports(true);
      try {
        const response = await httpClient.get('api/theft-report/get-by-location', {
          locationId: Number(locationId),
          dateFilter: serviceDate
        });
        
        if (response.data?.theftReports) {
          setTheftReports(response.data.theftReports);
        }
      } catch (error) {
        console.error('Failed to load theft reports:', error);
        setTheftReports([]);
      } finally {
        setIsLoadingTheftReports(false);
      }
    };

    loadTheftReports();
  }, [locationId, serviceDate]);

  // Invalidate and refetch data when component mounts or when locationId/serviceDate changes
  useEffect(() => {
    if (locationId && serviceDate) {
      // Invalidate queries to ensure fresh data is fetched
      queryClient.invalidateQueries({ 
        queryKey: ['location-readings', locationId, serviceDate] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['goa-events', 'current-day', locationId, serviceDate] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['goa-events', 'previous', locationId, serviceDate] 
      });
    }
  }, [locationId, serviceDate, queryClient]);

  // Process location data when it changes
  const serviceData = locationData ? (() => {
    if (!locationData) {
      navigate('/app/location-service-history');
      return null;
    }

    console.log("=== LOCATION DATA PROCESSING ===");
    console.log("locationData received:", locationData);
    console.log("all goaEvents from API:", locationData.goaEvents);

    // Normalize dates to YYYY-MM-DD format for comparison (avoid timezone issues)
    const normalizeDate = (date: Date | string) => {
      // If it's already in YYYY-MM-DD format, return it
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toISOString().split('T')[0];
    };
    
    const serviceDateNormalized = normalizeDate(serviceDate!);
    
    console.log("=== DATE FILTERING DEBUG ===");
    console.log("serviceDate from URL:", serviceDate);
    console.log("serviceDateNormalized:", serviceDateNormalized);
    console.log("All goaEvents dates:", locationData.goaEvents.map((e: any) => ({
      original: e.date,
      normalized: e.date ? normalizeDate(e.date) : 'NO DATE',
      hardIn: e.hardIn,
      hardOut: e.hardOut
    })));
    
    let filteredGoaEvents = locationData.goaEvents.filter((e: any) => {
      if (!e.date) return false;
      const eventDateNormalized = normalizeDate(e.date);
      const matches = eventDateNormalized === serviceDateNormalized;
      if (matches) {
        console.log("MATCHED EVENT:", { eventDate: e.date, normalized: eventDateNormalized, hardIn: e.hardIn, hardOut: e.hardOut });
      }
      return matches;
    });

    console.log("filtered goaEvents for serviceDate:", filteredGoaEvents);
    console.log("serviceDate being used:", serviceDate);

    // Filter GOAs: show active ones always, show inactive ones only if they have existing events
    const goaIdsWithEvents = new Set(filteredGoaEvents.map((e: any) => e.goaId));
    const filteredGoas = locationData.goas.filter((g: any) => {
      // Always show active GOAs
      if (g.active !== false) {
        return true;
      }
      // For inactive GOAs, only show if there's an existing event
      return goaIdsWithEvents.has(g.id);
    });

    const processedData = {
      goas: orderBy(filteredGoas.map((g: any) => ({ ...g, serviceable: true })), 'position', 'asc'),
      trks: orderBy(locationData.trks || [], 'position', 'asc'),
      atms: orderBy(locationData.atms || [], 'position', 'asc'),
      ctms: locationData.ctms,
      goaEvents: orderBy(filteredGoaEvents, (event: any) => {
        const goa = filteredGoas.find((g: any) => g.id === event.goaId);
        return goa ? goa.position : 0;
      }, 'asc'),
      trkEvents: locationData.trkEvents,
      atmEvents: locationData.atmEvents,
      ctmEvents: locationData.ctmEvents,
    };
    
    console.log("=== PROCESSED SERVICE DATA ===");
    console.log("Final goas count:", processedData.goas.length);
    console.log("Final goaEvents count:", processedData.goaEvents.length);
    console.log("GOA Events details:", processedData.goaEvents.map((e: any) => ({
      id: e.id,
      goaId: e.goaId,
      assetId: e.assetId,
      hardIn: e.hardIn,
      hardOut: e.hardOut
    })));
    
    return processedData;
  })() : null;

  // Refetch data when component mounts or when locationId/serviceDate changes
  useEffect(() => {
    if (locationId && serviceDate) {
      // Refetch location readings to ensure fresh data
      refetchLocationData();
    }
  }, [locationId, serviceDate, refetchLocationData]);

  // Log when locationData changes
  useEffect(() => {
    console.log("=== LOCATION DATA CHANGED ===");
    console.log("locationData:", locationData);
    console.log("isLoading:", isLoading);
    console.log("error:", error);
    if (locationData) {
      console.log("locationData.goaEvents:", locationData.goaEvents);
      console.log("locationData.locationVisitId:", locationData.locationVisitId);
    }
  }, [locationData, isLoading, error]);

  // Navigate if no locationId or serviceDate
  useEffect(() => {
    if (!locationId || !serviceDate) {
      navigate('/app/location-service-history');
    }
  }, [locationId, serviceDate, navigate]);

  // Navigate if data is not found
  useEffect(() => {
    if (locationData === null && !isLoading) {
      navigate('/app/location-service-history');
    }
  }, [locationData, isLoading, navigate]);

  const [breadcrumbs] = useState([
    { label: 'Home', href: '/app/dashboard' },
    { label: `${location?.name || ''}`, href: '/app/locations/' + locationId },
    { label: `Service - ${serviceDate}` },
  ]);

  const generateReportHandler = () => {
    setReportModalVisible(true);
    generateReport.mutate({
      locationId: Number(locationId),
      preview: true,
      serviceDate: serviceDate,
    }, {
      onSuccess: (data) => {
        setHtml(data.html);
      },
      onError: (error) => {
        console.error('Failed to generate report:', error);
        setReportModalVisible(false);
      }
    });
  };

  const exportTableToCsv = (baseName: string, headers: string[], rows: any[]) => {
    const sanitizeCell = (value: any) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      const needsQuotes = /[",\r\n]/.test(str);
      const escaped = str.replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    };

    const headerRow = headers.map(sanitizeCell).join(',');
    const dataRows = rows.map((row) => row.map(sanitizeCell).join(','));
    const csvContent = [headerRow, ...dataRows].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeBaseName = baseName.replace(/\s+/g, '-').toLowerCase();
    link.href = url;
    link.setAttribute('download', `${safeBaseName}-${serviceDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportReportToCsv = () => {
    if (!serviceData) return;

    const sanitizeCell = (value: any) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      const needsQuotes = /[",\r\n]/.test(str);
      const escaped = str.replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    };

    const formatDate = (date: Date | string | null | undefined): string => {
      if (!date) return '';
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };

    const formatDateTime = (date: Date | string | null | undefined): string => {
      if (!date) return '';
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const rows: string[] = [];

    // Header section
    rows.push('Service Report');
    rows.push('');
    rows.push(`Store,${sanitizeCell(location?.name || '')}`);
    rows.push(`Location,${sanitizeCell(location?.name || '')}`);
    rows.push(`Date,${sanitizeCell(formattedDisplayDate)}`);
    rows.push(`Time,${sanitizeCell(new Date().toLocaleTimeString())}`);
    rows.push(`Last Collection,${sanitizeCell(formattedDisplayDate)}`);
    rows.push(`Collector,${sanitizeCell('')}`); // TODO: Get collector name if available
    rows.push(`Route,${sanitizeCell(location?.name || '')}`);
    rows.push('');

    // GOAs/Terminals section with calculated fields
    if (serviceData.goas.length > 0) {
      rows.push('Terminals');
      rows.push('Order,Asset ID,Money In,Money Out,Total Net,Hard In,Hard Out,Net,Commission,Service Issue');
      
      let totalMoneyIn = 0;
      let totalMoneyOut = 0;
      let totalCommission = 0;
      
      serviceData.goas.forEach((goa: any) => {
        const event = serviceData.goaEvents.find((e: any) => e.goaId === goa.id);
        const previousEvent = goaEventsData?.previousEvents?.[goa.id as keyof typeof goaEventsData.previousEvents];
        
        // Current values
        const currentHardIn = event?.hardIn != null ? parseFloat(String(event.hardIn)) : 0;
        const currentHardOut = event?.hardOut != null ? parseFloat(String(event.hardOut)) : 0;
        
        // Previous values
        const previousHardIn = previousEvent?.hardIn != null ? parseFloat(String(previousEvent.hardIn)) : 0;
        const previousHardOut = previousEvent?.hardOut != null ? parseFloat(String(previousEvent.hardOut)) : 0;
        
        // Calculated fields
        const moneyIn = currentHardIn - previousHardIn;
        const moneyOut = currentHardOut - previousHardOut;
        const totalNet = moneyIn - moneyOut;
        const net = currentHardIn - currentHardOut;
        const commission = totalNet * ((location?.commissionPercentage || 0) / 100);
        
        // Accumulate totals
        totalMoneyIn += moneyIn;
        totalMoneyOut += moneyOut;
        totalCommission += commission;
        
        rows.push([
          goa.position || '',
          goa.assetId || '',
          moneyIn.toFixed(2),
          moneyOut.toFixed(2),
          totalNet.toFixed(2),
          currentHardIn.toFixed(2),
          currentHardOut.toFixed(2),
          net.toFixed(2),
          commission.toFixed(2),
          event?.serviceIssue || 'None',
        ].map(sanitizeCell).join(','));
      });
      
      // Totals section
      rows.push('');
      rows.push('Totals');
      rows.push('Category,Amount');
      rows.push(`Money In,${totalMoneyIn.toFixed(2)}`);
      rows.push(`Money Out,${totalMoneyOut.toFixed(2)}`);
      const totalNet = totalMoneyIn - totalMoneyOut;
      rows.push(`Total Net,${totalNet.toFixed(2)}`);
      rows.push(`Commission,${totalCommission.toFixed(2)}`);
      const defectiveTickets = 0; // TODO: Get actual defective tickets if available
      rows.push(`Defective Tickets,${defectiveTickets.toFixed(2)}`);
      const totalCommissionWithDefective = totalCommission + defectiveTickets;
      rows.push(`Total Commission,${totalCommissionWithDefective.toFixed(2)}`);
      const payToLocation = totalNet - totalCommissionWithDefective; // Pay to Location = Total Net - (Commission + Defective Tickets)
      rows.push(`Pay to Location,${payToLocation.toFixed(2)}`);
      rows.push('');
    }

    // TRKs section
    if (serviceData.trks.length > 0) {
      rows.push('TRKs');
      rows.push('Order,Asset ID,Cassette 1 ($1),Cassette 2 ($5),Cassette 3 ($20),Cassette 4 ($100),Total Load,Balance at Departure');
      serviceData.trks.forEach((trk: any) => {
        const event = serviceData.trkEvents.find((e: any) => e.trkId === trk.id);
        const totalLoad = event?.totalCassetteLoad != null ? parseFloat(String(event.totalCassetteLoad)) : 0;
        const balanceAtDeparture = event?.balanceAtDeparture != null ? parseFloat(String(event.balanceAtDeparture)) : 0;
        rows.push([
          trk.position || '',
          trk.assetId || trk.id || '',
          event?.cassette1Load || 0,
          event?.cassette2Load || 0,
          event?.cassette3Load || 0,
          event?.cassette4Load || 0,
          totalLoad.toFixed(2),
          balanceAtDeparture.toFixed(2),
        ].map(sanitizeCell).join(','));
      });
      rows.push('');
    }

    // ATMs section
    if (serviceData.atms.length > 0) {
      rows.push('ATMs');
      rows.push('Order,Asset ID,Load Amount,Balance at Departure,Start Amount');
      serviceData.atms.forEach((atm: any) => {
        const event = serviceData.atmEvents.find((e: any) => e.atmId === atm.id);
        const load = event?.load != null ? parseFloat(String(event.load)) : 0;
        const balanceAtDeparture = event?.balanceAtDeparture != null ? parseFloat(String(event.balanceAtDeparture)) : 0;
        const startAmount = event?.startAmount != null ? parseFloat(String(event.startAmount)) : 0;
        rows.push([
          atm.position || '',
          atm.assetId || atm.id || '',
          load.toFixed(2),
          balanceAtDeparture.toFixed(2),
          startAmount.toFixed(2),
        ].map(sanitizeCell).join(','));
      });
      rows.push('');
    }

    // CTMs section (if available)
    if (serviceData.ctms && serviceData.ctms.length > 0) {
      rows.push('CTMs');
      rows.push('Asset ID,Details');
      serviceData.ctms.forEach((ctm: any) => {
        const event = serviceData.ctmEvents?.find((e: any) => e.ctmId === ctm.id);
        rows.push([
          ctm.assetId || ctm.id || '',
          JSON.stringify(event || {}),
        ].map(sanitizeCell).join(','));
      });
      rows.push('');
    }

    // Outages section
    if (outages && outages.length > 0) {
      rows.push('Outages');
      rows.push('Outage Date,Outage Reason,Resolution,Resolve Date,Technician,Asset Type,Asset ID');
      outages.forEach((outage: any) => {
        let assetType = '';
        let assetId = '';
        if (outage.goaId) {
          assetType = 'GOA';
          assetId = String(outage.goaId);
        } else if (outage.trkId) {
          assetType = 'TRK';
          assetId = String(outage.trkId);
        } else if (outage.atmId) {
          assetType = 'ATM';
          assetId = String(outage.atmId);
        }
        rows.push([
          formatDateTime(outage.outageDate),
          outage.outageReason || '',
          outage.resolution || '',
          formatDateTime(outage.resolveDate),
          outage.technician || '',
          assetType,
          assetId,
        ].map(sanitizeCell).join(','));
      });
      rows.push('');
    }

    const csvContent = rows.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeLocationName = (location?.name || 'location').replace(/\s+/g, '-').toLowerCase();
    link.href = url;
    link.setAttribute('download', `service-report-${safeLocationName}-${serviceDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderTable = (title: string, headers: string[], rows: any[], editHandler?: () => void, csvName?: string) => (
    <Box sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box>
          <IconButton
            size="small"
            onClick={() => exportTableToCsv(csvName || title, headers, rows)}
            title="Download CSV"
            sx={{ mr: editHandler ? 1 : 0 }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          {editHandler && (
            <IconButton onClick={editHandler} title="Edit">
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Divider />
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map((header, idx) => (
                <TableCell key={idx}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={idx}
                hover
                sx={{ cursor: 'pointer' }}
              >
                {row.map((cell: any, cidx: number) => (
                  <TableCell key={cidx}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  if (!serviceData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  return (
    <div>
      <TitleBar
        title={`Service Details - ${location?.name} (${formattedDisplayDate})`}
        breadcrumbs={breadcrumbs}
      />
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Location Overview</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PreviewIcon />}
              onClick={generateReportHandler}
              disabled={generateReport.isPending}
              sx={{ minWidth: 200 }}
            >
              {generateReport.isPending ? 'Generating...' : 'Generate Report'}
            </Button>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2"><strong>Location:</strong> {location?.name}</Typography>
          <Typography variant="body2"><strong>Service Date:</strong> {formattedDisplayDate}</Typography>
        </Box>

        {serviceData.goas.length > 0 && renderTable(
          'GOAs',
          ['Order', 'Asset ID', 'Hard In', 'Hard Out', 'Service Issue'],
          serviceData.goas.map((goa: any) => {
            const event = serviceData.goaEvents.find((e: any) => e.goaId === goa.id);
            const previousEvent = goaEventsData?.previousEvents?.[goa.id as keyof typeof goaEventsData.previousEvents];
            
            // Calculate money in/out based on previous values
            const moneyIn = previousEvent?.hardIn ? (event?.hardIn || 0) - previousEvent.hardIn : 0;
            const moneyOut = previousEvent?.hardOut ? (event?.hardOut || 0) - previousEvent.hardOut : 0;
            const net = moneyIn - moneyOut;
            
            return [
              goa.position || '-',
              goa.assetId,
              formatCurrency(event?.hardIn),
              formatCurrency(event?.hardOut),
              event?.serviceIssue || 'None',
            ];
          }),
          () => setBulkEditVisible(true),
          'goas'
        )}

        {serviceData.trks.length > 0 && renderTable(
          'TRKs',
          ['Order', 'Asset ID', 'Cassette 1 ($1)', 'Cassette 2 ($5)', 'Cassette 3 ($20)', 'Cassette 4 ($100)', 'Total Load', 'Balance at Departure'],
          serviceData.trks.map((trk: any) => {
            const event = serviceData.trkEvents.find((e: any) => e.trkId === trk.id);
            return [
              trk.position || '-',
              trk.assetId || trk.id,
              event?.cassette1Load || 0,
              event?.cassette2Load || 0,
              event?.cassette3Load || 0,
              event?.cassette4Load || 0,
              formatCurrency(event?.totalCassetteLoad),
              formatCurrency(event?.balanceAtDeparture),
            ];
          }),
          () => setBulkEditTrkVisible(true),
          'trks'
        )}

        {serviceData.atms.length > 0 && renderTable(
          'ATMs',
          ['Order', 'Asset ID', 'Load Amount', 'Balance at Departure', 'Start Amount'],
          serviceData.atms.map((atm: any) => {
            const event = serviceData.atmEvents.find((e: any) => e.atmId === atm.id);
            return [
              atm.position || '-',
              atm.assetId || atm.id,
              formatCurrency(event?.load),
              formatCurrency(event?.balanceAtDeparture),
              formatCurrency(event?.startAmount),
            ];
          }),
          () => setBulkEditAtmVisible(true),
          'atms'
        )}

        {/* Outages Section - only show if there's at least one */}
        {outages && outages.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h6" gutterBottom>Outages</Typography>
            <Divider sx={{ mb: 2 }} />
            <OutageTable outages={outages} type="service" assetId={0} />
          </Box>
        )}

        {/* Theft Reports Section - only show if there's at least one */}
        {theftReports.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h6" gutterBottom>Theft Reports</Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Asset Type</strong></TableCell>
                    <TableCell><strong>Asset ID</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Comments</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {theftReports.map((report: any) => {
                    let assetType = '';
                    let assetId = '';
                    
                    if (report.goaId) {
                      assetType = 'GOA';
                      assetId = String(report.goaId);
                    } else if (report.trkId) {
                      assetType = 'TRK';
                      assetId = String(report.trkId);
                    } else if (report.atmId) {
                      assetType = 'ATM';
                      assetId = String(report.atmId);
                    }

                    const reportDate = new Date(report.date);
                    const formattedDate = reportDate.toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });

                    return (
                      <TableRow key={report.id}>
                        <TableCell>{formattedDate}</TableCell>
                        <TableCell>{assetType}</TableCell>
                        <TableCell>{assetId}</TableCell>
                        <TableCell>{formatCurrency(report.amount)}</TableCell>
                        <TableCell>{report.comments || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Saved Reports Section */}
        <SavedReportsSection locationId={locationId!} serviceDate={serviceDate!} />

        {/* Site Visit Photos Section */}
        {locationVisitId && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h6" gutterBottom>Site Visit Photos</Typography>
            <Divider sx={{ mb: 2 }} />
            {isLoadingPhotos ? (
              <Typography variant="body2">Loading photos...</Typography>
            ) : visitPhotos.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No photos available for this site visit
              </Typography>
            ) : (
              <ImageList sx={{ width: '100%', height: 'auto' }} cols={4} rowHeight={200}>
                {visitPhotos.map((photo) => (
                  <ImageListItem 
                    key={photo.id}
                    sx={{ position: 'relative', cursor: 'pointer' }}
                    onClick={() => photo.signedUrl && window.open(photo.signedUrl, '_blank')}
                  >
                    {photo.signedUrl ? (
                      <img
                        src={photo.signedUrl}
                        alt={photo.context || 'Site visit photo'}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.200'
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Loading...
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        p: 0.5,
                        fontSize: '0.75rem'
                      }}
                    >
                      {photo.context === 'black-box' ? 'Black Box' : 'Game Accounting'}
                    </Box>
                    {/* Asset ID Label */}
                    {(() => {
                      const assetInfo = getAssetInfo(photo);
                      if (assetInfo.assetId) {
                        return (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              bgcolor: 'rgba(255, 255, 255, 0.9)',
                              color: 'text.primary',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'medium'
                            }}
                          >
                            {assetInfo.assetId}
                          </Box>
                        );
                      }
                      return null;
                    })()}
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        )}
      </Box>

      <GenericFormModal
        visible={bulkEditVisible && serviceData?.goas?.length > 0}
        onDismiss={() => setBulkEditVisible(false)}
        disableBackdropClose
        showCloseIcon
        onSubmit={async (values: any) => {
          console.log('=== GOA EDIT FORM SUBMIT START ===');
          console.log('1. Form submitted with values:', values);
          console.log('2. serviceData.goas:', serviceData.goas);
          console.log('3. serviceData.goaEvents:', serviceData.goaEvents);
          console.log('4. locationId:', locationId);
          console.log('5. serviceDate:', serviceDate);
          console.log('6. locationVisitId:', locationVisitId);

          // Always use all GOAs as source of truth (not just ones with events)
          // This ensures GOAs with 0 values that were skipped by backend still appear
          const goaValues = serviceData.goas.map((goa: any) => {
            const event = serviceData.goaEvents.find((e: any) => e.goaId === goa.id);
            const goaId = goa.id;
            
            // Get values from form, defaulting to 0 if not provided
            const hardIn = parseFloat(values[`hardIn_${goaId}`]);
            const hardOut = parseFloat(values[`hardOut_${goaId}`]);
            
            console.log(`7. Processing GOA ${goa.assetId} (id: ${goaId}):`, {
              formHardIn: values[`hardIn_${goaId}`],
              parsedHardIn: hardIn,
              formHardOut: values[`hardOut_${goaId}`],
              parsedHardOut: hardOut,
              existingEvent: event,
              finalHardIn: isNaN(hardIn) ? '0' : hardIn.toString(),
              finalHardOut: isNaN(hardOut) ? '0' : hardOut.toString()
            });
            
            // Backend expects strings for hardIn and hardOut
            return {
              goaId: goaId,
              hardIn: isNaN(hardIn) ? '0' : hardIn.toString(),
              hardOut: isNaN(hardOut) ? '0' : hardOut.toString(),
              locationId: Number(locationId),
              assetId: goa.assetId || '',
              event: event?.event || GOA_EVENT_ENTRY_TYPE.CASH_PULL, // Default event type
              serviceIssue: event?.serviceIssue || ''
            };
          });

          const dispatchRequest: any = {
            goas: goaValues,
            serviceDate: serviceDate, // Send serviceDate so backend can use it instead of new Date()
          };

          // Backend requires locationVisitId to find existing events for updates
          // Try to get it from multiple sources:
          // 1. State (set from locationData)
          // 2. Existing events
          // 3. locationData directly
          let finalLocationVisitId = locationVisitId;
          
          // Try to get from existing events
          if (!finalLocationVisitId && serviceData.goaEvents.length > 0) {
            const firstEvent = serviceData.goaEvents[0];
            if (firstEvent.locationVisitId) {
              finalLocationVisitId = firstEvent.locationVisitId;
              console.log('8a. Found locationVisitId from existing event:', finalLocationVisitId);
              setLocationVisitId(finalLocationVisitId);
            }
          }
          
          // Try to get from locationData
          if (!finalLocationVisitId && locationData?.locationVisitId) {
            finalLocationVisitId = locationData.locationVisitId;
            console.log('8a. Found locationVisitId from locationData:', finalLocationVisitId);
            setLocationVisitId(finalLocationVisitId);
          }
          
          // Send locationVisitId if we have it, otherwise backend will get/create one for serviceDate
          if (finalLocationVisitId) {
            dispatchRequest.locationVisitId = finalLocationVisitId;
            console.log('8b. Using locationVisitId:', finalLocationVisitId);
          } else {
            console.log('8b. No locationVisitId found - backend will get/create one for serviceDate:', serviceDate);
            // Backend will handle getting/creating locationVisit for serviceDate
            dispatchRequest.locationVisitId = 0; // Backend will replace this with actual locationVisit.id
          }

          console.log('8. Final dispatchRequest:', JSON.stringify(dispatchRequest, null, 2));
          console.log('9. About to dispatch upsertGoaEvents...');

          // Use upsert action which handles both create and update
          upsertGoaEventsMutation.mutate(dispatchRequest, {
            onSuccess: async (data) => {
              console.log('=== UPSERT CALLBACK FIRED ===');
              console.log('10. Upsert response data:', data);
              console.log('11. Response type:', typeof data);
              console.log('12. Response keys:', data ? Object.keys(data) : 'null/undefined');
              console.log('12a. Response locationVisit:', data?.locationVisit);
              console.log('12b. Response goaEvents count:', data?.goaEvents?.length);
              console.log('12c. Response goaEvents details:', data?.goaEvents?.map((e: any) => ({
                id: e.id,
                goaId: e.goaId,
                assetId: e.assetId,
                hardIn: e.hardIn,
                hardOut: e.hardOut,
                date: e.date,
                locationVisitId: e.locationVisitId
              })));
              
              // Update locationVisitId from response if provided
              if (data?.locationVisit?.id) {
                console.log('12d. Setting locationVisitId from response:', data.locationVisit.id);
                setLocationVisitId(data.locationVisit.id);
              }
              
              // Check if events have correct date
              if (data?.goaEvents?.length > 0) {
                const firstEvent = data.goaEvents[0];
                const eventDate = firstEvent.date ? new Date(firstEvent.date).toISOString().split('T')[0] : null;
                console.log('12e. First event date:', eventDate, 'vs serviceDate:', serviceDate);
                if (eventDate !== serviceDate) {
                  console.warn('12f. WARNING: Event date does not match serviceDate! Backend may have saved with wrong date.');
                }
              }
              
              setBulkEditVisible(false);
              console.log('13. Modal closed, starting data refresh...');
              
              try {
                // Refetch queries to ensure data is updated immediately
                console.log('14. Refetching location-readings query...');
                const locationRefetch = await queryClient.refetchQueries({ 
                  queryKey: ['location-readings', locationId, serviceDate] 
                });
                console.log('15. Location refetch result:', locationRefetch);
              
              console.log('16. Refetching goa-events queries...');
              const goaRefetch = await queryClient.refetchQueries({ 
                predicate: (query) => query.queryKey[0] === 'goa-events' 
              });
              console.log('17. GOA events refetch result:', goaRefetch);
              
              console.log('18. Calling manual refetch functions...');
              const manualLocationRefetch = await refetchLocationData();
              console.log('19. Manual location refetch result:', manualLocationRefetch);
              
              const manualGoaRefetch = await refetchGoaEvents();
              console.log('20. Manual GOA refetch result:', manualGoaRefetch);
              
              console.log('=== DATA REFRESH COMPLETE ===');
            } catch (error) {
              console.error('ERROR during data refresh:', error);
            }
          },
          onError: (error: any) => {
            console.error('Failed to update GOA events:', error);
          },
        });
          
          console.log('21. Mutation called, waiting for callback...');
        }}
        initialValues={(() => {
          const initialVals: Record<string, any> = {};
          
          // Always use all GOAs as source of truth (not just ones with events)
          // This ensures GOAs with 0 values that were skipped by backend still appear
          serviceData.goas.forEach((goa: any) => {
            const event = serviceData.goaEvents.find((e: any) => e.goaId === goa.id);
            const goaId = goa.id;
            
            initialVals[`hardIn_${goaId}`] = event?.hardIn || 0;
            initialVals[`hardOut_${goaId}`] = event?.hardOut || 0;
          });

          console.log('Final initialValues object:', initialVals);
          console.log('Final locationVisitId:', locationVisitId);
          return initialVals;
        })()}
        fieldGroups={[{
          groupName: 'GOA Events',
          fields: serviceData.goas.flatMap((goa: any) => {
            const event = serviceData.goaEvents.find((e: any) => e.goaId === goa.id);
            const goaId = goa.id;
            const assetId = goa.assetId;
            const position = goa.position;
            const assetLabel = position != null ? `Asset ID (${position})` : 'Asset ID';
            
            return [
              {
                label: assetLabel,
                name: `assetId_${goaId}`, // Use different name to avoid conflict
                type: GENERIC_FORM_FIELD_TYPE.REACT_ELEMENT,
                required: false,
                columns: 4,
                component: <div key={`asset-${goaId}`}>{assetId}</div>
              },
              {
                label: 'Hard In',
                name: `hardIn_${goaId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 4,
              },
              {
                label: 'Hard Out',
                name: `hardOut_${goaId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 4,
              },
            ];
          }),
        }]}
        title="Bulk Edit GOAs"
        maxWidth={1000}
      />

      {/* Bulk Edit TRK Modal */}
      <GenericFormModal
        visible={bulkEditTrkVisible && serviceData?.trks?.length > 0}
        onDismiss={() => setBulkEditTrkVisible(false)}
        disableBackdropClose
        showCloseIcon
        onSubmit={async (values: any) => {
          console.log('=== TRK EDIT FORM SUBMIT START ===');
          console.log('1. Form submitted with values:', values);
          console.log('2. serviceData.trks:', serviceData.trks);
          console.log('3. serviceData.trkEvents:', serviceData.trkEvents);

          const trkValues = serviceData.trks.map((trk: any) => {
            const event = serviceData.trkEvents.find((e: any) => e.trkId === trk.id);
            const trkId = trk.id;
            
            const cassette1Load = parseFloat(values[`cassette1Load_${trkId}`]) || 0;
            const cassette2Load = parseFloat(values[`cassette2Load_${trkId}`]) || 0;
            const cassette3Load = parseFloat(values[`cassette3Load_${trkId}`]) || 0;
            const cassette4Load = parseFloat(values[`cassette4Load_${trkId}`]) || 0;
            const balanceAtDeparture = parseFloat(values[`balanceAtDeparture_${trkId}`]) || 0;
            
            return {
              trkId: trkId,
              cassette1Load: cassette1Load.toString(),
              cassette2Load: cassette2Load.toString(),
              cassette3Load: cassette3Load.toString(),
              cassette4Load: cassette4Load.toString(),
              balanceAtDeparture: balanceAtDeparture.toString(),
              locationId: Number(locationId),
              assetId: trk.assetId || '',
            };
          });

          let finalLocationVisitId = locationVisitId;
          
          if (!finalLocationVisitId && serviceData.trkEvents.length > 0) {
            const firstEvent = serviceData.trkEvents[0];
            if (firstEvent.locationVisitId) {
              finalLocationVisitId = firstEvent.locationVisitId;
              setLocationVisitId(finalLocationVisitId);
            }
          }
          
          if (!finalLocationVisitId && locationData?.locationVisitId) {
            finalLocationVisitId = locationData.locationVisitId;
            setLocationVisitId(finalLocationVisitId);
          }
          
          if (!finalLocationVisitId) {
            finalLocationVisitId = 0; // Backend will handle getting/creating one
          }

          const dispatchRequest: any = {
            locationVisitId: finalLocationVisitId,
            locationId: Number(locationId),
            trks: trkValues.map(trk => ({
              id: trk.trkId,
              trkId: trk.trkId,
              assetId: trk.assetId,
              cassette1Load: trk.cassette1Load,
              cassette2Load: trk.cassette2Load,
              cassette3Load: trk.cassette3Load,
              cassette4Load: trk.cassette4Load,
              balanceAtDeparture: trk.balanceAtDeparture,
              serviceIssue: '' // TRK doesn't have serviceIssue but backend expects it
            })),
          };

          console.log('Final TRK dispatchRequest:', JSON.stringify(dispatchRequest, null, 2));

          upsertTrkEventsMutation.mutate(dispatchRequest, {
            onSuccess: async (data) => {
              console.log('TRK Upsert response:', data);
              
              if (data?.locationVisit?.id) {
                setLocationVisitId(data.locationVisit.id);
              }
              
              setBulkEditTrkVisible(false);
              
              try {
                await queryClient.refetchQueries({ 
                  queryKey: ['location-readings', locationId, serviceDate] 
                });
                await queryClient.refetchQueries({ 
                  predicate: (query) => query.queryKey[0] === 'trk-events' 
                });
                await refetchLocationData();
              } catch (error) {
                console.error('ERROR during data refresh:', error);
              }
            },
            onError: (error: any) => {
              console.error('Failed to update TRK events:', error);
            },
          });
        }}
        initialValues={(() => {
          const initialVals: Record<string, any> = {};
          
          serviceData.trks.forEach((trk: any) => {
            const event = serviceData.trkEvents.find((e: any) => e.trkId === trk.id);
            const trkId = trk.id;
            
            initialVals[`cassette1Load_${trkId}`] = event?.cassette1Load || 0;
            initialVals[`cassette2Load_${trkId}`] = event?.cassette2Load || 0;
            initialVals[`cassette3Load_${trkId}`] = event?.cassette3Load || 0;
            initialVals[`cassette4Load_${trkId}`] = event?.cassette4Load || 0;
            initialVals[`balanceAtDeparture_${trkId}`] = event?.balanceAtDeparture || 0;
          });

          return initialVals;
        })()}
        fieldGroups={[{
          groupName: 'TRK Events',
          fields: serviceData.trks.flatMap((trk: any) => {
            const event = serviceData.trkEvents.find((e: any) => e.trkId === trk.id);
            const trkId = trk.id;
            const assetId = trk.assetId;
            const position = trk.position;
            const assetLabel = position != null ? `Asset ID (${position})` : 'Asset ID';
            
            return [
              {
                label: assetLabel,
                name: `assetId_${trkId}`,
                type: GENERIC_FORM_FIELD_TYPE.REACT_ELEMENT,
                required: false,
                columns: 3,
                component: <div key={`asset-${trkId}`}>{assetId}</div>
              },
              {
                label: 'Cassette 1 ($1)',
                name: `cassette1Load_${trkId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 2,
              },
              {
                label: 'Cassette 2 ($5)',
                name: `cassette2Load_${trkId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 2,
              },
              {
                label: 'Cassette 3 ($20)',
                name: `cassette3Load_${trkId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 2,
              },
              {
                label: 'Cassette 4 ($100)',
                name: `cassette4Load_${trkId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 2,
              },
              {
                label: 'Balance at Departure',
                name: `balanceAtDeparture_${trkId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 2,
              },
            ];
          }),
        }]}
        title="Bulk Edit TRKs"
        maxWidth={1200}
      />

      {/* Bulk Edit ATM Modal */}
      <GenericFormModal
        visible={bulkEditAtmVisible && serviceData?.atms?.length > 0}
        onDismiss={() => setBulkEditAtmVisible(false)}
        disableBackdropClose
        showCloseIcon
        onSubmit={async (values: any) => {
          console.log('=== ATM EDIT FORM SUBMIT START ===');
          console.log('1. Form submitted with values:', values);
          console.log('2. serviceData.atms:', serviceData.atms);
          console.log('3. serviceData.atmEvents:', serviceData.atmEvents);

          const atmValues = serviceData.atms.map((atm: any) => {
            const event = serviceData.atmEvents.find((e: any) => e.atmId === atm.id);
            const atmId = atm.id;
            
            const load = parseFloat(values[`load_${atmId}`]) || 0;
            const balanceAtDeparture = parseFloat(values[`balanceAtDeparture_${atmId}`]) || 0;
            const startAmount = parseFloat(values[`startAmount_${atmId}`]) || 0;
            
            return {
              atmId: atmId,
              load: load.toString(),
              balanceAtDeparture: balanceAtDeparture.toString(),
              startAmount: startAmount.toString(),
              locationId: Number(locationId),
              assetId: atm.assetId || '',
            };
          });

          let finalLocationVisitId = locationVisitId;
          
          if (!finalLocationVisitId && serviceData.atmEvents.length > 0) {
            const firstEvent = serviceData.atmEvents[0];
            if (firstEvent.locationVisitId) {
              finalLocationVisitId = firstEvent.locationVisitId;
              setLocationVisitId(finalLocationVisitId);
            }
          }
          
          if (!finalLocationVisitId && locationData?.locationVisitId) {
            finalLocationVisitId = locationData.locationVisitId;
            setLocationVisitId(finalLocationVisitId);
          }
          
          if (!finalLocationVisitId) {
            finalLocationVisitId = 0; // Backend will handle getting/creating one
          }

          const dispatchRequest: any = {
            locationVisitId: finalLocationVisitId,
            events: atmValues.map(atm => ({
              id: atm.atmId,
              assetId: atm.assetId,
              locationId: Number(locationId),
              event: 'Cash Fill', // Default event type
              load: atm.load,
              balanceAtDeparture: atm.balanceAtDeparture,
              startAmount: atm.startAmount,
            })),
          };

          console.log('Final ATM dispatchRequest:', JSON.stringify(dispatchRequest, null, 2));

          upsertAtmEventsMutation.mutate(dispatchRequest, {
            onSuccess: async (data) => {
              console.log('ATM Upsert response:', data);
              
              if (data?.locationVisit?.id) {
                setLocationVisitId(data.locationVisit.id);
              }
              
              setBulkEditAtmVisible(false);
              
              try {
                await queryClient.refetchQueries({ 
                  queryKey: ['location-readings', locationId, serviceDate] 
                });
                await queryClient.refetchQueries({ 
                  predicate: (query) => query.queryKey[0] === 'atm-events' 
                });
                await refetchLocationData();
              } catch (error) {
                console.error('ERROR during data refresh:', error);
              }
            },
            onError: (error: any) => {
              console.error('Failed to update ATM events:', error);
            },
          });
        }}
        initialValues={(() => {
          const initialVals: Record<string, any> = {};
          
          serviceData.atms.forEach((atm: any) => {
            const event = serviceData.atmEvents.find((e: any) => e.atmId === atm.id);
            const atmId = atm.id;
            
            initialVals[`load_${atmId}`] = event?.load || 0;
            initialVals[`balanceAtDeparture_${atmId}`] = event?.balanceAtDeparture || 0;
            initialVals[`startAmount_${atmId}`] = event?.startAmount || 0;
          });

          return initialVals;
        })()}
        fieldGroups={[{
          groupName: 'ATM Events',
          fields: serviceData.atms.flatMap((atm: any) => {
            const event = serviceData.atmEvents.find((e: any) => e.atmId === atm.id);
            const atmId = atm.id;
            const assetId = atm.assetId;
            const position = atm.position;
            const assetLabel = position != null ? `Asset ID (${position})` : 'Asset ID';
            
            return [
              {
                label: assetLabel,
                name: `assetId_${atmId}`,
                type: GENERIC_FORM_FIELD_TYPE.REACT_ELEMENT,
                required: false,
                columns: 4,
                component: <div key={`asset-${atmId}`}>{assetId}</div>
              },
              {
                label: 'Load Amount',
                name: `load_${atmId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 4,
              },
              {
                label: 'Balance at Departure',
                name: `balanceAtDeparture_${atmId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 4,
              },
              {
                label: 'Start Amount',
                name: `startAmount_${atmId}`,
                type: GENERIC_FORM_FIELD_TYPE.FLOAT,
                required: false,
                columns: 4,
              },
            ];
          }),
        }]}
        title="Bulk Edit ATMs"
        maxWidth={1000}
      />
      {/* Report Modal */}
      <Modal
        open={reportModalVisible}
        onClose={() => setReportModalVisible(false)}
        aria-labelledby="report-modal-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 0,
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '100%',
            height: '100%',
            overflow: 'auto',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <Typography variant="h6" id="report-modal-title">
              Service Report - {location?.name} ({formattedDisplayDate})
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {html && (
                <IconButton
                  onClick={exportReportToCsv}
                  size="large"
                  title="Download CSV"
                >
                  <DownloadIcon />
                </IconButton>
              )}
              <IconButton
                onClick={() => setReportModalVisible(false)}
                size="large"
                title="Close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            {generateReport.isPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Typography variant="h6">Generating report...</Typography>
              </Box>
            ) : html ? (
              <Box
                sx={{
                  ...(isDark && {
                    '& *': { color: '#fff !important' },
                    '& td': { borderBottomColor: 'rgba(255,255,255,0.2) !important' },
                    '& .summary': { background: 'rgba(255,255,255,0.12) !important' },
                  }),
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: html }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Typography variant="h6" color="error">
                  Failed to generate report
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}

export default LocationServiceViewPage;
